import type { ScreenApp, ScreenAppAttribute, ScreenAppManifest } from './types'

/**
 * The pure logic behind the Screen apps page (admin/ScreenApps.tsx) and the
 * "Screen app" placement (admin/equipment/ScreenAppSection.tsx): which of an
 * organisation's apps a machine may run, the manifest a placement's form is built
 * from, and turning that form's raw values — a checkbox, a number typed as text —
 * into what PUT /units/{id}/app expects. The server checks every value again
 * (screenapp.CheckAttributes); this only saves a round trip for a mistake the form
 * could have caught itself.
 */

/** An app may run on a machine of its own kind, and only until it is retired. A
 *  retired app is refused by the server (APP_RETIRED); left out here so the select
 *  never offers a choice the server would refuse. */
export function placeableApps(apps: readonly ScreenApp[], kind: 'kiosk' | 'display'): ScreenApp[] {
  return apps.filter((app) => app.kind === kind && !app.retiredAt)
}

/** The manifest of the version a machine placed on this app actually runs. Absent
 *  only for data that cannot exist once an app has been uploaded at all: every app
 *  has a current version from its first upload onward. */
export function currentManifest(app: ScreenApp): ScreenAppManifest | undefined {
  return app.versions.find((v) => v.id === app.currentVersionId)?.manifest
}

/** The short form of a version's checksum the version list shows: enough of it
 *  that two versions are never mistaken for each other, short enough that the row
 *  does not wrap. */
export function shortSHA(sha256: string): string {
  return sha256.slice(0, 12)
}

function rawOf(type: ScreenAppAttribute['type'], value: unknown): string | boolean {
  if (type === 'boolean') return Boolean(value)
  return value === undefined || value === null ? '' : String(value)
}

/** An attribute form's raw starting values: text for a number or a string field, a
 *  boolean for a checkbox — one entry per key the given manifest declares. The
 *  placement's own value is kept only when one is on record; a key with none, or
 *  the manifest's attributes changed since, takes the manifest's default — the
 *  same rule the server's own Reconcile applies when a new version changes what a
 *  machine carries. */
export function attributeFormValues(
  manifest: ScreenAppManifest | undefined,
  current: Record<string, unknown> | undefined,
): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {}
  for (const attr of manifest?.attributes ?? []) {
    const value = current && attr.key in current ? current[attr.key] : attr.default
    out[attr.key] = rawOf(attr.type, value)
  }
  return out
}

/** Turns one attribute's raw form value into what the server expects: a checkbox
 *  is already a boolean, and a number typed as text is parsed — an empty or
 *  unparseable one becomes 0 rather than NaN, a value the server would refuse as
 *  the wrong type in a sentence the form could have said itself. */
export function typedAttributeValue(type: ScreenAppAttribute['type'], raw: string | boolean): string | number | boolean {
  if (type === 'boolean') return Boolean(raw)
  if (type === 'integer') {
    const n = parseInt(String(raw).trim(), 10)
    return Number.isFinite(n) ? n : 0
  }
  return String(raw)
}

/** The attributes object PUT /units/{id}/app expects, built from a form's raw
 *  values against the manifest that names each attribute's key and type. */
export function typedAttributes(
  manifest: ScreenAppManifest | undefined,
  values: Record<string, string | boolean>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  for (const attr of manifest?.attributes ?? []) {
    out[attr.key] = typedAttributeValue(attr.type, values[attr.key] ?? '')
  }
  return out
}

/** Whether an integer attribute's text is a whole number the server will accept —
 *  checked before Save, so a value that would otherwise be silently rewritten to 0
 *  (an empty field, "3.5", "12kg") is refused with a message instead. Signed, since
 *  a manifest may declare a negative default. */
export function isValidInteger(raw: string): boolean {
  return /^-?\d+$/.test(raw.trim())
}

/** How to show a placement whose app cannot be offered as a live choice — because
 *  it has since been retired, or because the licence no longer includes the
 *  module and so the catalogue cannot be read at all. Either way the point is the
 *  same: the machine still runs it, it cannot be chosen again, and the only
 *  sensible action left is to take it off. Falls back to the id itself when even
 *  the name is unreadable, so there is still something to show and to clear. */
export function placedAppName(app: ScreenApp | undefined, appId: string): { name: string; retired: boolean } {
  if (app && !app.retiredAt) return { name: app.name, retired: false }
  return { name: app?.name ?? appId, retired: true }
}
